/**
 * CAOS-1683 — a roster surface reached as an APP TAB's destination.
 *
 * A roster was reachable two ways: as a package root, or as a capability claim (the Setup
 * contribution path in `setup-surface.ts`). Neither is a route an ordinary app can author, so a
 * `rosterPage` could be built, installed, and have nowhere to be opened from. The operator-facing
 * feedback triage list is reached by a capability claim; the reporter-facing equivalent replaces an
 * app tab, and a tab's `target` could not name a roster at all.
 *
 * This module is the third route, and it is deliberately thin: it resolves the spec and hands it to
 * the SAME renderers the other two routes use. Nothing about how a roster draws is re-decided here,
 * because a second implementation of that is how two routes to one surface start disagreeing.
 *
 * WHY A FETCH RATHER THAN A LOOKUP IN `OrgMetadata`. The bulk metadata read carries `pages` but no
 * roster bodies, and the shipped roster routes all resolve theirs individually through
 * `/api/metadata/component-get` (four local copies of that call exist, in `setup-surface.ts`,
 * `setup-component-library.ts`, `setup-object-manager.ts` and `setup-seats.ts`). Following that
 * rather than adding roster bodies to the bulk payload: putting every roster spec into the metadata
 * every reader loads on every navigation is a cost paid by everyone to save a fetch for whoever opens
 * one, and this change is not the place to make that call.
 *
 * The fetch is why this returns a node that fills in, rather than a promise. `resolveShellContentNode`
 * is synchronous (`Node | undefined`), and the pattern for this already exists in `setup-surface.ts`:
 * return the container now, replace its children when the spec arrives.
 */
import { renderRosterPage, placeInConsolePane } from './roster-page.js';
import { renderRosterDetailPage } from './roster-detail-page.js';
import type { RosterPageSpec, RosterFetch } from './roster-page.js';
import type { RosterDetailPageSpec } from './roster-detail-page.js';

/** The fetch this module uses, matching the host's own injectable shape so tests can supply one. */
export type RosterDestinationFetch = (input: string, init?: RequestInit) => Promise<Response>;

interface ComponentGetEnvelope {
  ok?: boolean;
  data?: { key?: unknown; body?: Record<string, unknown> };
}

/**
 * Resolve one installed component body by type and key.
 *
 * Returns `null` for every failure — a transport error, a non-ok envelope, a missing body — because
 * the caller's answer is the same in all of them: say the destination has nothing behind it. What it
 * must NOT do is throw, which would take down the whole content node and leave the reader with a
 * blank frame instead of a sentence.
 */
async function fetchSpec<T>(
  type: 'rosterPage' | 'rosterDetailPage',
  key: string,
  doFetch: RosterDestinationFetch,
): Promise<T | null> {
  let env: ComponentGetEnvelope;
  try {
    const res = await doFetch(`/api/metadata/component-get?type=${type}&key=${encodeURIComponent(key)}`);
    env = (await res.json()) as ComponentGetEnvelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  const envelope = env.data;
  if (!envelope || typeof envelope.key !== 'string' || !envelope.body) return null;
  return { ...envelope.body, key: envelope.key } as unknown as T;
}

/**
 * How this seam says a destination is absent: the CALLER's fallback renderer, passed in.
 *
 * Not imported from `content-source.ts`, although that is where `fallbackNode` lives and where the
 * sibling branches use it. Importing it would close a cycle — `content-source` imports this module —
 * and not duplicating it matters more than either: a roster that cannot be read must read EXACTLY like
 * a page that cannot be read (CAOS-388) or a record type that is not installed (CAOS-503), because to
 * the person looking at it, it is the same event. A second local copy of that pane is how the two
 * drift apart one wording change at a time.
 */
export type RenderFallback = (title: string, body: string) => HTMLElement;

/**
 * The default, used only when no caller supplies one — a plain pane with the same elements
 * `fallbackNode` builds, so a direct caller in a test sees real text rather than an attribute.
 */
const defaultFallback: RenderFallback = (title, body) => {
  const wrap = document.createElement('div');
  wrap.className = 'sa-surface sa-surface--fallback';
  const h = document.createElement('h1');
  h.className = 'sa-fallback-title';
  h.textContent = title;
  const p = document.createElement('p');
  p.className = 'sa-fallback-body';
  p.textContent = body;
  wrap.append(h, p);
  return wrap;
};

/**
 * A roster that cannot be read is indistinguishable, to the reader, from one that is empty — so
 * saying nothing here would be the silent blank CAOS-388 and CAOS-503 each exist to rule out. The
 * sentence names what is missing and who can fix it, as those two branches do.
 */
function nothingBehindIt(render: RenderFallback, label: string, kind: string, key: string): HTMLElement {
  return render(
    `“${label}” has nothing behind it`,
    `This tab opens a ${kind} called “${key}”, and this workspace does not have it. ` +
      'Install the package that provides it, or point the tab somewhere else.',
  );
}

/**
 * Draw an app tab's roster destination.
 *
 * `kind` is the arm the tab authored, and it is passed through rather than inferred: the kernel
 * refuses a tab whose arm disagrees with the target's actual kind
 * (`validation.tab_target_kind`), so by the time a tab is installed the arm IS the kind, and
 * guessing here would be a second, weaker answer to a question already settled at deploy.
 *
 * A `rosterDetailPage` destination reached as a TAB is drawn with no addressed id, and that is not a
 * limitation of this seam. The kernel refuses a tab pointing at a detail page that declares `idParam`
 * (`validation.tab_target_addressed_detail_page`), because a tab has no record to supply, so the only
 * detail page that can be a tab's destination is a SINGLETON — and `renderRosterDetailPage` already
 * ignores a passed id for one of those ("SINGLETON mode (no idParam) has no addressed id at all —
 * never mind what the caller passed"). The two layers agree rather than one compensating for the
 * other.
 *
 * CAOS-1764 — a detail page reached as a ROW, at `/<app>/<tab>/<id>`, is the other case, and it is
 * the one a tab's emptiness was never a statement about. The address supplies the record the tab
 * could not, so `ctx.recordId` is passed through to the same renderer. The kernel's refusal is
 * untouched and still right: the TAB here points at the `rosterPage`, and the detail page is resolved
 * from the roster's own `breadcrumbRootHref` rather than named by the tab at all.
 *
 * Returns a container that fills in when the spec arrives, plus the promise a test can await.
 */
export function renderRosterDestination(
  kind: 'rosterPage' | 'rosterDetailPage',
  key: string,
  label: string,
  ctx: {
    readonly navigate?: (href: string) => void;
    readonly fetch?: RosterDestinationFetch;
    /** How to say the destination is absent; the caller passes its own, so every branch agrees. */
    readonly renderFallback?: RenderFallback;
    /**
     * CAOS-1764 — the line ABOVE the roster's heading, when the route knows something the spec
     * cannot.
     *
     * A roster spec is written once and reached from more than one place. `my_feedback_list` is the
     * `feedback` app's landing tab AND the My Profile console's Feedback section, and the context
     * line differs between them — which is precisely why it belongs to the ROUTE and not to the
     * spec. Putting "My Profile" in the spec would print it on the app tab, where it is false.
     *
     * Absent ⇒ the spec's own `eyebrow`, which is the behaviour every existing caller had and
     * keeps. Supplied ⇒ it wins, because a route that knows its own context knows it better than a
     * component authored without one.
     */
    readonly eyebrow?: string;
    /**
     * CAOS-1764 — the record this address names, when a `rosterDetailPage` is reached as a ROW
     * rather than as a tab. Absent ⇒ no addressed record, which is every tab destination and the
     * only thing a SINGLETON detail page can be. Meaningless with `kind: 'rosterPage'`, which draws
     * the list itself.
     */
    readonly recordId?: string;
  },
): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const doFetch = ctx.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));
  const renderFallback = ctx.renderFallback ?? defaultFallback;

  /**
   * The roster's OWN row reads go through the same injected fetch, not through its default.
   *
   * `renderRosterPage` falls back to `defaultRosterFetch` when given none, which calls the global
   * `fetch` directly. That would mean this seam honours an injected fetch for resolving the SPEC and
   * then silently ignores it for the rows — two different transports for one surface, which in a test
   * shows up as a real network attempt and in any embedding that injects a fetch would show up as a
   * roster that cannot read anything. `credentials` is carried because that is what the default adds
   * and the rows are an authenticated read.
   */
  const rosterFetch: RosterFetch = (path) =>
    doFetch(path, { credentials: 'same-origin' }) as unknown as ReturnType<RosterFetch>;

  /**
   * CAOS-1764 — every write to `root` goes through this, so the console inset lands on exactly one
   * element. `root` must carry it while it holds the "Loading…" state or a nothing-behind-it
   * fallback, and must NOT once it holds a surface that is already a pane. See
   * {@link placeInConsolePane} for what that was doubling and how it was measured.
   */
  const place = (child: Node): void => placeInConsolePane(root, child);

  /**
   * NOTHING HERE MAY ESCAPE, and the reason is one line up the call stack: `content-source.ts` takes
   * `.root` and discards this promise, so a rejection would be unhandled AND would leave whatever is
   * in `root` at that moment on screen for good.
   *
   * `fetchSpec` already swallows its own failures, but the RENDER step can throw too — a spec that
   * satisfies `component-get` but not the renderer's expectations is a malformed-metadata case, not an
   * impossible one. The two failure points strand the reader differently, which is why this catches
   * around both rather than only guarding the renderer call:
   *
   *   - a synchronous throw from `renderRosterPage` happens BEFORE `replaceChildren`, so the reader is
   *     left on "Loading…" permanently — the worst outcome, because it reads as a slow network and
   *     invites waiting;
   *   - a rejection from `await drawn` happens after, so a half-drawn surface stays up instead.
   *
   * Both get the same sentence the absent case gets. A surface that cannot be drawn and one that is
   * not installed are the same event to the person looking at it (CAOS-388, CAOS-503).
   */
  const done = (async (): Promise<void> => {
    try {
      if (kind === 'rosterPage') {
        const spec = await fetchSpec<RosterPageSpec>('rosterPage', key, doFetch);
        if (!spec) {
          place(nothingBehindIt(renderFallback, label, 'list', key));
          return;
        }
        // CAOS-1764 — the route's eyebrow overrides the spec's, and is spread so an absent one
        // leaves the spec exactly as authored rather than overwriting it with `undefined`.
        const routed = ctx.eyebrow === undefined ? spec : { ...spec, eyebrow: ctx.eyebrow };
        const { root: surface, done: drawn } = renderRosterPage(routed, { navigate: ctx.navigate, fetch: rosterFetch });
        place(surface);
        await drawn;
        return;
      }

      const spec = await fetchSpec<RosterDetailPageSpec>('rosterDetailPage', key, doFetch);
      if (!spec) {
        place(nothingBehindIt(renderFallback, label, 'page', key));
        return;
      }
      const { root: surface, done: drawn } = renderRosterDetailPage(spec, ctx.recordId, { navigate: ctx.navigate, fetch: rosterFetch });
      place(surface);
      await drawn;
    } catch {
      // The pane is built by a CALLER-SUPPLIED renderer, so this line can throw for the same reason the
      // body did. Without the inner guard the rejection escapes exactly as before and the reader is left
      // on "Loading…" — the failure this catch exists to prevent, reintroduced by the catch itself. A
      // text node needs nothing injected and cannot fail, so the last resort is a plain sentence rather
      // than a styled pane. No logging here: the host has no console calls anywhere in `src`, and every
      // other catch in it swallows and renders a fallback.
      try {
        place(nothingBehindIt(renderFallback, label, kind === 'rosterPage' ? 'list' : 'page', key));
      } catch {
        place(document.createTextNode(`“${label}” has nothing behind it.`));
      }
    }
  })();

  return { root, done };
}
